import { Moment } from 'moment';

export interface IHomeViewFilters {
  createdAt: [Moment, Moment];
}

export interface IPagination {
  currentPage: number;
  pageSize: number;
  total: number;
}

// ─── Incident Ticket API Types ────────────────────────────────────────────────

export interface IIncidentTicketAssignee {
  _id: string;
  name: string;
  email: string;
  empId: string;
  isAgent: boolean;
}

export interface IIncidentTicketDetails {
  incidentType: 'LIVE' | 'EVENT';
  _id: string;
  priority: 'high' | 'medium' | 'low';
}

export interface IIncidentTicket {
  _id: string;
  alertName: string;
  summary: string;
  objectType: string;
  objectId?: string;
  hubCode?: string | null;
  ticketMetaData?: {
    currentHubCode?: string | null;
    riderCode?: string | null;
    vendorCode?: string | null;
    vendorName?: string | null;
    vehicleCode?: string | null;
    consignmentNumbers?: string[] | null;
    tripReferenceNumber?: string | null;
    customerName?: string | null;
    customerCode?: string | null;
    customerCategory?: string | null;
  };
  aggregateData?: {
    tripNumber?: string | null;
  };
  assignee: IIncidentTicketAssignee;
  ticketDetails: IIncidentTicketDetails;
  ticketId: string;
  status: 'open' | 'acknowledged' | 'actionTaken' | 'closed';
  resolutionSla?: string;
  closedAt?: string;
  metricType: string;
  metricName: string;
  metricId: string;
  value?: string;
  threshold?: number;
  creationTime: string;
  commentsCount: number;
  isHitlPresent: boolean;
  isHitlActionTaken: boolean;
}

export interface IIncidentTicketsApiResponse {
  status: boolean;
  data: {
    ticketsCount: number;
    tickets: IIncidentTicket[];
    priorityCounts: Record<string, number>;
    statusCounts: Record<string, number>;
  };
}

export interface IConfigFormField {
  label: string;
  value: string;
}

// FE-special config types. `metrics`→cards, `heatmap`→header; anything else (or no
// type) is a generic chart rendered by the report's own response `chartType`.
export type AgentReportType = 'metrics' | 'heatmap';

// Response `chartType`s the generic ReportChart renders (html/pivotTable fall back).
export type ReportChartType = 'pie' | 'bar' | 'line' | 'number' | 'table' | 'progressBar' | 'dualAxes';

// A report reference in config_json.reports, rendered in page order.
export interface IAgentReportRef {
  id: string;
  type?: AgentReportType;
}

export interface IDashboardReportRef {
  reportId: string;
  chartType?: string;
  reportName?: string;
  pageSize?: number;
  chartConfig?: Record<string, any>;
  filtersIdOverRideValue?: Record<string, boolean>;
  position?: { x: number; y: number; w: number; h: number };
}

export interface IDashboardGroupTab {
  id: string;
  name: string;
  reports: IDashboardReportRef[];
}

export interface IAgentOptionConfigJson {
  reports?: IAgentReportRef[];
  report_id?: string;        // legacy single-report fallback
  dashboard_group_id?: string; // drives the chart grid when present
  color?: string;
  icon?: string;
  description?: string;
  trigger?: string;
  form?: Record<string, IConfigFormField>;
  [key: string]: any;
}

// One day's bucket for the activity heatmap.
export interface IHeatmapPoint {
  date: string;
  count: number;
}

// One outcome slice for the resolution pie.
export interface IResolutionSlice {
  label: string;
  value: number;
  percentage?: number;
}

// One {x, y} point for bar / line / area charts.
export interface ISeriesPoint {
  x: string;
  y: number;
}

export interface IAgentOption {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  config_json?: IAgentOptionConfigJson;
}

// ─── Report Data API Types ──────────────────────────────────────────────────

export interface IReportMetric {
  id: string;
  metric: string;
  label: string;
  value: number;
  format: 'number' | 'percent';
}

export interface IReportDataParams {
  reportId: string;
  filters?: Array<{
    metric: string;
    filter: {
      name: string;
      data: string;
      type: string;
    };
  }>;
}

export interface IReportMetricMeta {
  _id: string;
  metric: string;
  alias: string;
  prettyName: string;
  type: string;
  index: number;
}

export interface IReportDataApiData {
  chartData: string;
  metrices: IReportMetricMeta[];
  lastRefreshTime: string;
  chartType?: string;
  reportDescription?: string;
  // Visualisation reports pre-shape chartData and carry plot field names here.
  formData?: string;
}

// ─── API Param / Body Types ──────────────────────────────────────────────────

export interface IGetTasksParams {
  metricType: string;
  pageSize: number;
  offSet?: number;
  status?: string;
  isHitlActionTaken?: boolean;
  isHitlPresent?: boolean;
  hubId?: string;
  priority?: string;
  creationTime?: string;
  assignee?: string;
  taskSupervisor?: string;
  createdBy?: string;
  selectedWatchers?: string;
  resolutionSla?: string;
  ticketId?: string;
  objectId?: string;
  sortBy?: string;
}

// ─── Status & Grouped Counts APIs (incident-alert-ticket) ──────────────────────

// Backend grouping dimensions for /grouped-counts (see HLD shipsy/query-builder#2457).
export type GroupByKey = 'agent' | 'metricType' | 'status' | 'priority' | 'assignee';

// Shared filter params accepted by both count endpoints — the same scope as the
// list endpoint, minus pagination. Arrays are JSON-stringified before sending.
export interface IGetCountsParams {
  metricType?: string;
  priority?: string;
  assignee?: string;
  status?: string;
  sla?: string;
  hubId?: string;
  creationTime?: string;
  isHitlPresent?: boolean;
  isHitlActionTaken?: boolean;
}

export interface IGetGroupedCountsParams extends IGetCountsParams {
  groupBy: GroupByKey;
  limit?: number;
}

export interface IStatusCountsResponse {
  status: boolean;
  data: {
    total: number;
    statusCounts: Record<string, number>;
    interventionsRequired: number;
  };
}

// Uniform grouped-counts item. `value` is the token fed back as a list filter;
// for assignee/agent `label` is the name and `value` is the empId.
export interface IGroupCountItem {
  label: string | null;
  value: string | null;
  count: number;
}

export interface IGroupedCountsResponse {
  status: boolean;
  data: {
    groupBy: string;
    total: number;
    items: IGroupCountItem[];
  };
}

// projectx saved-view filter catalogue entry (full_filter_metadata). Drives the
// Tasks filter bar widgets and the query-builder param each filter sends.
export interface ITaskFilterMeta {
  id: string;
  label?: string;            // display name (backend); falls back to pretty_name
  pretty_name?: string;
  param?: string;            // query-builder ticket-list param to send
  type?: string;             // 'input' | 'select' | 'search' | 'date'
  mode?: 'single' | 'multiple' | 'range';
  options?: Array<{ label: string; value: any }>;
  search_strategy?: string;  // for type 'search'
  param_format?: string;     // 'date_range' → value maps to { from, to }
  threshold_length?: number;
  [key: string]: unknown;
}

// Resolved per-user filter selection (filter_list_by_bucket).
export interface ITaskFilterSelection {
  id: string;
  show_on_main_dashboard?: boolean;
}

// Runtime filter values applied in the bar, keyed by filter id.
export type ITaskFilterValues = Record<string, any>;

// Fixed (non-customisable) scope passed alongside the dynamic filters when
// building the ticket-list query params.
export interface IBuildTasksParamsOptions {
  createdAt?: [Moment, Moment];
  statusFilter?: string[];
  hubId?: string | null;
  hubCode?: string | null;
  offSet?: number;
  hitlParams?: { isHitlActionTaken: boolean; isHitlPresent: boolean };
  // Drill-down from a selected Group By bucket — applied as a list/count filter.
  groupFilter?: { groupBy: GroupByKey; value: string };
  // Logged-in user's empId — backs me-relative scope filters (param_format
  // 'current_user_scope'), e.g. "Assigned to me" / "Supervising" / "Watching".
  currentUserEmpId?: string;
}

export interface IFetchMetricTypesParams {
  pageCount: number;
  q?: string;
}

export interface ISearchEmployeeBody {
  operationName: string;
  variables: { searchQuery: string };
  query: string;
}

export interface IFetchMyActivityFeedParams {
  limit: number;
  from?: string;
  to?: string;
  entityType?: string;
  entityId?: string;
  lastCreationTime?: string;
  view?: 'all' | 'assigned' | 'supervising' | 'created' | 'watching';
}

export interface IFetchAgentDetailParams {
  workflow_id: string;
}

export interface ITaskConversationParams {
  order?: 'asc' | 'desc';
  limit?: number;
  // Present only for a load-more call: page a single session past `cursor`.
  session_id?: number;
  cursor?: number;
}

// ─── Object binding ───────────────────────────────────────────────────────────
// Shared by the create-task modal and the task type editor.
export const OBJECT_TYPE_CUSTOM = 'Custom';

export interface IObjectTypeOption {
  key: string;
  label: string;
}

export interface IObjectBinding {
  defaultObjectType?: string;
  additionalObjectTypes?: string[];
  validateObjectCode?: boolean;
}

export interface IGeofenceBinding {
  enabled?: boolean;
  boundObject?: string;
  radiusField?: string;
}

export interface IGeofenceBoundObject {
  key: string;
  label: string;
  radiusOptions?: { key: string; label: string }[];
}
